// @ts-check

import axios from "axios";

// ─── 설정 ─────────────────────────────────────────────────────────────────────
//
// BASE_URL     : cms-backend 서버 주소
// CONTENT_BASE : 공개 홈페이지 API prefix  (/api/public/homepage)
//               분석 경로는 CONTENT_BASE/analytics 로 파생되므로 별도 상수 없음
//
const BASE_URL     = (process.env.REACT_APP_CMS_API_URL || "").replace(/\/+$/, "");
const CONTENT_BASE = (process.env.REACT_APP_CMS_HOMEPAGE_API_PREFIX || "/api/public/homepage").replace(/\/+$/, "");

// ─── 엔드포인트 상수 ───────────────────────────────────────────────────────────
//
// 실제 요청 경로를 한눈에 볼 수 있도록 모아둔다.
// 최종 URL = BASE_URL + 아래 경로
//   예) GET https://lcms-dev.lumir.space/api/public/homepage/news
//
const ENDPOINT = {
  // 콘텐츠 (공개, 인증 불필요)
  NEWS:         `${CONTENT_BASE}/news`,                  // 루미르 뉴스
  PRESS:        `${CONTENT_BASE}/press-releases`,        // 보도자료
  DISCLOSURES:  `${CONTENT_BASE}/electronic-disclosures`, // 전자공시
  VIDEOS:       `${CONTENT_BASE}/video-galleries`,       // 비디오 갤러리
  BROCHURES:    `${CONTENT_BASE}/brochures`,             // 브로슈어
  MEETINGS:     `${CONTENT_BASE}/shareholders-meetings`, // 주주총회
  IR:           `${CONTENT_BASE}/irs`,                   // IR 자료
  POPUPS:       `${CONTENT_BASE}/main-popups`,           // 홈 팝업 (공지)
  // 분석 (공개, 인증 불필요)
  PAGE_VIEWS:   `${CONTENT_BASE}/analytics/page-views`,
};

// ─── axios 인스턴스 ────────────────────────────────────────────────────────────
const cmsClient = axios.create({
  baseURL: BASE_URL || undefined,
  headers: { "Content-Type": "application/json" },
});

// ─── 공통 유틸리티 ─────────────────────────────────────────────────────────────

/**
 * "KR" / "kr" / "kor" → "ko", "EN" / "en" / "eng" → "en"
 * 컴포넌트의 langState("KR"/"EN") 값을 BE 쿼리 파라미터 규격으로 변환한다.
 */
const toLangCode = (language) => {
  const v = String(language || "").toLowerCase();
  if (v === "kr" || v === "ko" || v === "kor") return "ko";
  if (v === "en" || v === "eng") return "en";
  return v || undefined;
};

/**
 * undefined / null / "" 값을 쿼리 파라미터에서 제외한다.
 * axios 는 undefined 를 자동 제외하지만, null 과 "" 도 제외하기 위해 사용한다.
 */
const compactParams = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

/**
 * 파일 URL 이 상대경로이면 BASE_URL 을 앞에 붙여 절대경로로 변환한다.
 * 이미 절대경로(https://, blob:, /) 이면 그대로 반환한다.
 */
const resolveFileUrl = (fileUrl) => {
  if (!fileUrl) return "";
  if (/^(blob:|data:|https?:\/\/)/i.test(fileUrl)) return fileUrl;
  const fileBase = (process.env.REACT_APP_CMS_FILE_BASE_URL || BASE_URL).replace(/\/+$/, "");
  const normalizedPath = fileUrl.replace(/^\/+/, "");

  if (fileBase) {
    return `${fileBase}/${normalizedPath}`;
  }

  return fileUrl.startsWith("/") ? fileUrl : `/${normalizedPath}`;
};

// ─── 콘텐츠 공통 요청 헬퍼 ────────────────────────────────────────────────────

/**
 * isPublic=true 목록을 조회한다.
 * @param {string} endpoint  ENDPOINT.* 상수 값
 * @param {object} options   { page, limit, language, orderBy?, keyword?, startDate?, endDate?, categoryId? }
 */
const getList = (endpoint, { page, limit, language, ...rest }) =>
  cmsClient
    .get(endpoint, {
      params: compactParams({
        isPublic: true,
        page,
        limit,
        language: toLangCode(language),
        ...rest,
      }),
    })
    .then((r) => r.data);

/**
 * 단건 상세를 조회한다.
 * @param {string} endpoint  ENDPOINT.* 상수 값
 * @param {string} id
 * @param {string} [language]
 */
const getById = (endpoint, id, language) =>
  cmsClient
    .get(`${endpoint}/${id}`, {
      params: compactParams({ language: toLangCode(language) }),
    })
    .then((r) => r.data);

// ─── 콘텐츠 도메인 API ─────────────────────────────────────────────────────────

export const cmsRepository = {
  // 유틸 (api.service.js 에서 사용)
  resolveFileUrl,
  getCmsLanguage: toLangCode,

  // 뉴스 (루미르 뉴스 / 루미르 스토리)
  getNews:      (page, limit, language, params = {}) =>
    getList(ENDPOINT.NEWS, { page, limit, language, orderBy: "createdAt", ...params }),
  getNewsById:  (id, language) => getById(ENDPOINT.NEWS, id, language),

  // 보도자료
  getPressReleases:   (page, limit, language, params = {}) =>
    getList(ENDPOINT.PRESS, { page, limit, language, orderBy: "displayDate", ...params }),
  getPressReleaseById: (id, language) => getById(ENDPOINT.PRESS, id, language),

  // 전자공시
  getDisclosures:   (page, limit, language, params = {}) =>
    getList(ENDPOINT.DISCLOSURES, { page, limit, language, orderBy: "displayDate", ...params }),
  getDisclosureById: (id, language) => getById(ENDPOINT.DISCLOSURES, id, language),

  // 비디오 갤러리
  getVideos:   (page, limit, language, params = {}) =>
    getList(ENDPOINT.VIDEOS, { page, limit, language, orderBy: "order", ...params }),
  getVideoById: (id, language) => getById(ENDPOINT.VIDEOS, id, language),

  // 브로슈어
  getBrochures:   (page, limit, language, params = {}) =>
    getList(ENDPOINT.BROCHURES, { page, limit, language, ...params }),
  getBrochureById: (id, language) => getById(ENDPOINT.BROCHURES, id, language),

  // 주주총회
  getMeetings:   (page, limit, language, params = {}) =>
    getList(ENDPOINT.MEETINGS, { page, limit, language, ...params }),
  getMeetingById: (id, language) => getById(ENDPOINT.MEETINGS, id, language),

  // IR 자료
  getIRMaterials:   (page, limit, language, params = {}) =>
    getList(ENDPOINT.IR, { page, limit, language, orderBy: "displayDate", ...params }),
  getIRMaterialById: (id, language) => getById(ENDPOINT.IR, id, language),

  // 팝업 (홈 메인 공지 팝업)
  getNotices:   (page, limit, language) =>
    getList(ENDPOINT.POPUPS, { page, limit, language }),
  getNoticeById: (id, language) => getById(ENDPOINT.POPUPS, id, language),
};

// ─── 분석 API ──────────────────────────────────────────────────────────────────

/** Date / 문자열 / falsy 값을 ISO 8601 문자열로 변환 */
const toIso = (value) => {
  if (!value) return new Date().toISOString();
  if (value instanceof Date) return value.toISOString();
  const d = new Date(value);
  return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
};

/** 전체 URL 에서 pathname + search 부분만 추출 */
const toPagePath = (raw) => {
  if (!raw) return "/";
  try {
    const u = new URL(raw, window.location.origin);
    return `${u.pathname}${u.search}`;
  } catch {
    return raw;
  }
};

export const analyticsRepository = {
  /**
   * 페이지 진입 시 방문 기록을 생성한다.
   * @returns {Promise<{ id: string }>} 생성된 page-view ID
   */
  createPageView: (params) => {
    const pagePath     = params.pagePath || toPagePath(params.url);
    const pageCategory = params.pageCategory || params.pageName || pagePath || "unknown";
    return cmsClient
      .post(ENDPOINT.PAGE_VIEWS, {
        sessionId:    params.sessionId,
        pageCategory,
        pagePath,
        contentTitle: params.contentTitle || params.title || pageCategory,
        enterTime:    toIso(params.enterTime),
      })
      .then((r) => r.data);
  },

  /**
   * 페이지 이탈 시 체류 시간을 기록한다.
   * keepalive 옵션 사용 시 브라우저 종료 직전에도 요청이 전송된다.
   */
  updatePageView: (params, options = {}) => {
    const body = {
      sessionId:     params.sessionId,
      pagePath:      params.pagePath || toPagePath(params.url),
      exitTime:      toIso(params.exitTime),
      stayDurationMs: params.stayDurationMs ?? params.stayDuration ?? 0,
    };

    if (options.keepalive && typeof fetch === "function") {
      // keepalive fetch : 탭 닫힘/새로고침 시에도 요청을 완료시킨다
      return fetch(`${BASE_URL}${ENDPOINT.PAGE_VIEWS}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        keepalive: true,
      });
    }

    return cmsClient.put(ENDPOINT.PAGE_VIEWS, body).then((r) => r.data);
  },
};

// ─── 하위 호환 export ──────────────────────────────────────────────────────────
// api.service.js 가 apiSDKRepository 를 import 하므로 별칭을 유지한다.
export const apiSDKRepository = cmsRepository;
